(() => {
  if (customElements.get('sticky-atc')) return;

  // Third-party corner widgets to lift above the bar: the 10% off tab (Alia) and the chat bubble (Re:amaze).
  // Only their small closed state is moved, never an opened pop-up or chat window.
  const WIDGETS = ['[id^="alia-root"]', '#reamaze-widget'];

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;

      this.form = document.querySelector('[data-product-form]');
      if (!this.form) return;

      // Live at the end of the page so no parent layout can affect position: fixed
      // (moving it re-runs this callback, which then carries on below)
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }
      this.ready = true;

      this.sizesEl = this.querySelector('[data-sizes]');
      this.addButton = this.querySelector('[data-add]');
      this.errorEl = this.querySelector('[data-error]');
      this.visible = false;
      this.inert = true;

      this.addButton.addEventListener('click', () => this.add());

      // The main form re-renders its content whenever a size is picked; mirror it each time
      new MutationObserver(() => this.sync()).observe(this.form, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      this.form.addEventListener('change', () => this.sync());
      this.sync();

      new ResizeObserver(() => this.updateOffset()).observe(this);

      this.onScroll = () => this.checkPosition();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.checkPosition();

      // Widgets load late; keep them clear of the bar while it's showing
      this.widgetTimer = setInterval(() => this.visible && this.placeWidgets(), 1000);
    }

    // Sizes in the product's own order (the main form lists pre-order sizes in their own groups)
    get radios() {
      const radios = Array.from(this.form.querySelectorAll('input[type="radio"][name="id"]'));
      const order = (this.dataset.variantOrder || '').split(',');
      const position = (radio) => {
        const i = order.indexOf(radio.value);
        return i === -1 ? order.length : i;
      };
      return radios.sort((a, b) => position(a) - position(b));
    }

    get mainButton() {
      return this.form.querySelector('button[type="submit"]');
    }

    sync() {
      const main = this.mainButton;
      if (main) this.addButton.textContent = main.textContent.trim();
      this.addButton.disabled = this.form.classList.contains('loading');
      if (this.selected()) this.showError(false);
      this.showPreorder();
      this.renderSizes();
    }

    // "PRE ORDER AVAILABLE ..." for the chosen size, taken from the main form for the shopper's market
    showPreorder() {
      const message = (this.selected()?.dataset.preorder || '').trim();
      this.classList.toggle('sticky-atc--preorder', Boolean(message));
      this.querySelectorAll('[data-preorder-msg]').forEach((el) => {
        el.textContent = message;
      });
    }

    // One row of sizes, mirroring the main form. If a size name ever appears in more than one
    // group, the bar can't tell them apart, so it sends shoppers up to the full picker instead.
    renderSizes() {
      if (!this.sizesEl) return;
      const radios = this.radios;
      const titles = radios.map((r) => this.labelFor(r)?.textContent.trim() || '');
      const grouped = new Set(titles).size !== titles.length;
      this.classList.toggle('sticky-atc--no-sizes', grouped || !radios.length);
      if (grouped) return;

      const key = radios.map((r) => `${r.value}:${r.checked}:${r.disabled}`).join('|');
      if (key === this.sizesKey) return;
      this.sizesKey = key;

      this.sizesEl.replaceChildren(
        ...radios.map((radio, i) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'sticky-atc__size';
          button.textContent = titles[i];
          button.disabled = radio.disabled;
          button.setAttribute('aria-pressed', String(radio.checked));
          button.addEventListener('click', () => {
            this.showError(false);
            // Select it in the main form without focusing it, so the page doesn't jump up
            const current = this.radios.find((r) => r.value === radio.value);
            if (!current || current.checked || current.disabled) return;
            current.checked = true;
            current.dispatchEvent(new Event('change', { bubbles: true }));
          });
          return button;
        })
      );
    }

    labelFor(radio) {
      return this.form.querySelector(`label[for="${radio.id}"]`);
    }

    selected() {
      return this.form.querySelector('input[name="id"]:checked, input[name="id"][type="hidden"]');
    }

    add() {
      const main = this.mainButton;
      if (!main) return;

      if (this.selected()) {
        main.click();
        return;
      }

      if (this.classList.contains('sticky-atc--no-sizes')) {
        // No size row in the bar: show the prompt in the main form and take the shopper to it
        main.click();
        const header = document.querySelector('header.header');
        const top = this.form.getBoundingClientRect().top + window.scrollY - (header ? header.offsetHeight : 0) - 24;
        window.scrollTo({ top, behavior: 'smooth' });
        return;
      }

      this.showError(true);
    }

    showError(show) {
      this.classList.toggle('sticky-atc--error', show);
    }

    // Visible once the main Add to cart button has scrolled up past the sticky header
    checkPosition() {
      const buttons = this.form.querySelector('.product-form__buttons') || this.form;
      const header = document.querySelector('header.header');
      const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
      const passed = buttons.getBoundingClientRect().bottom < headerBottom;
      if (passed !== this.visible) this.setVisible(passed);
    }

    setVisible(show) {
      this.visible = show;
      this.classList.toggle('is-visible', show);
      this.inert = !show;
      document.documentElement.classList.toggle('sticky-atc-open', show);
      if (!show) this.showError(false);
      this.updateOffset();
    }

    // Space the bar takes up from the bottom of the screen: its height plus any gap below it
    // (on desktop it floats 20px up as a box)
    get lift() {
      if (!this.visible) return 0;
      return this.offsetHeight + (parseFloat(getComputedStyle(this).bottom) || 0);
    }

    updateOffset() {
      document.documentElement.style.setProperty('--sticky-atc-height', `${this.lift}px`);
      this.placeWidgets();
    }

    placeWidgets() {
      const lift = this.lift;
      const bar = this.getBoundingClientRect();
      WIDGETS.forEach((selector) => {
        const el = document.querySelector(selector);
        if (!el) return;
        if (el.dataset.stickyAtcBase === undefined) {
          const base = parseFloat(getComputedStyle(el).bottom);
          if (Number.isNaN(base)) return;
          el.dataset.stickyAtcBase = base;
        }
        const rect = el.getBoundingClientRect();
        const isSmall = rect.height > 0 && rect.height < 120 && rect.width < 420;
        // Only move a widget the bar would actually cover (the desktop box usually clears both corners)
        const overlaps = rect.right > bar.left && rect.left < bar.right;
        const base = parseFloat(el.dataset.stickyAtcBase);
        if (lift && isSmall && overlaps) {
          if (!el.style.transition) el.style.setProperty('transition', 'bottom 0.45s cubic-bezier(0.65, 0, 0.35, 1)');
          el.style.setProperty('bottom', `${base + lift}px`, 'important');
        } else if (el.style.getPropertyPriority('bottom') === 'important') {
          el.style.setProperty('bottom', `${base}px`);
        }
      });
    }
  }

  customElements.define('sticky-atc', StickyAtc);
})();